import React, { useState, useEffect } from "react";
import { 
  Shield, 
  ShieldAlert, 
  ShieldCheck, 
  Search, 
  Download, 
  FileText, 
  Filter, 
  Calendar, 
  RefreshCw, 
  Loader2, 
  ChevronDown, 
  ChevronUp, 
  AlertTriangle,
  Lock,
  UserCheck,
  Activity
} from "lucide-react";
import { db } from "../firebase/config";
import { collection, query, orderBy, limit, onSnapshot } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { parseFirestoreTimestamp, formatAuditTimestamp, toISOTimestamp } from "../utils/dateUtils";

export default function AuditLogs() {
  const { currentUser } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [severityFilter, setSeverityFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [timeFilter, setTimeFilter] = useState("7d"); // '24h', '7d', '30d', 'all'
  const [expandedLogId, setExpandedLogId] = useState(null);

  useEffect(() => {
    setLoading(true);
    // Real-time snapshot listener on immutable audit_logs collection
    const q = query(
      collection(db, "audit_logs"),
      orderBy("timestamp", "desc"),
      limit(500)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const records = [];
      snapshot.forEach((doc) => {
        records.push({ id: doc.id, ...doc.data() });
      });
      setLogs(records);
      setLoading(false);
    }, (err) => {
      console.error("Error subscribing to audit logs:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Filter logs based on search, severity, category, and date range
  const filteredLogs = logs.filter((log) => {
    // Time filter
    if (timeFilter !== "all") {
      const parsedDate = parseFirestoreTimestamp(log.timestamp, log.clientTimestamp);
      if (parsedDate) {
        const logTime = parsedDate.getTime();
        const now = Date.now();
        const hoursMap = { "24h": 24, "7d": 24 * 7, "30d": 24 * 30 };
        const maxAgeMs = (hoursMap[timeFilter] || 24 * 7) * 60 * 60 * 1000;
        if (now - logTime > maxAgeMs) return false;
      }
    }

    // Severity filter
    if (severityFilter !== "All" && log.severity !== severityFilter) {
      return false;
    }

    // Category filter
    if (categoryFilter !== "All" && log.category !== categoryFilter) {
      return false;
    }

    // Search query
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      const matchAction = (log.action || "").toLowerCase().includes(q);
      const matchUser = (log.userEmail || "").toLowerCase().includes(q);
      const matchDetails = JSON.stringify(log.details || {}).toLowerCase().includes(q);
      if (!matchAction && !matchUser && !matchDetails) return false;
    }

    return true;
  });

  // Calculate high-level compliance metrics
  const totalEvents = filteredLogs.length;
  const criticalCount = filteredLogs.filter(l => l.severity === "CRITICAL" || l.severity === "WARNING").length;
  const mfaEventsCount = filteredLogs.filter(l => (l.action || "").includes("MFA")).length;
  const failedLoginsCount = filteredLogs.filter(l => l.action === "AUTH_LOGIN_FAILURE" || l.action === "AUTH_BRUTE_FORCE_DETECTED").length;

  // CSV Export for Compliance
  const handleExportCSV = () => {
    if (filteredLogs.length === 0) return;

    const headers = ["Timestamp (UTC / ISO)", "Action", "Category", "Severity", "User Email", "Status", "Details"];
    const rows = filteredLogs.map((l) => [
      `"${toISOTimestamp(l.timestamp, l.clientTimestamp)}"`,
      `"${l.action || ""}"`,
      `"${l.category || ""}"`,
      `"${l.severity || ""}"`,
      `"${l.userEmail || ""}"`,
      `"${l.status || ""}"`,
      `"${JSON.stringify(l.details || {}).replace(/"/g, '""')}"`
    ]);

    const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `jimini-soc2-audit-logs-${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // JSON Export for Auditors
  const handleExportJSON = () => {
    if (filteredLogs.length === 0) return;

    const normalizedLogs = filteredLogs.map((l) => ({
      ...l,
      timestampISO: toISOTimestamp(l.timestamp, l.clientTimestamp),
      formattedTimestamp: formatAuditTimestamp(l.timestamp, l.clientTimestamp)
    }));

    const exportData = {
      complianceStandard: "SOC 2 Type 2 / ISO/IEC 27001 / CMMC Level 1",
      exportedAt: new Date().toISOString(),
      exportedBy: currentUser?.email || "System Admin",
      totalRecords: normalizedLogs.length,
      logs: normalizedLogs
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `jimini-audit-evidence-${new Date().toISOString().split("T")[0]}.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSeverityBadgeStyle = (severity) => {
    switch (severity) {
      case "CRITICAL":
        return { backgroundColor: "rgba(239, 68, 68, 0.2)", color: "#ef4444", border: "1px solid #ef4444" };
      case "WARNING":
        return { backgroundColor: "rgba(255, 152, 0, 0.2)", color: "#ff9800", border: "1px solid #ff9800" };
      case "INFO":
      default:
        return { backgroundColor: "rgba(76, 175, 80, 0.2)", color: "#10b981", border: "1px solid #10b981" };
    }
  };

  return (
    <div className="animate-fade-in" style={{ padding: "1.5rem", maxWidth: "1400px", margin: "0 auto" }}>
      {/* Page Header */}
      <div className="page-header" style={{ marginBottom: "1.5rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
        <div className="page-header-info">
          <h1 className="page-title" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <Shield className="sidebar-logo-icon" style={{ color: "var(--primary)" }} />
            Security & Compliance Audit Logs
          </h1>
          <p className="page-subtitle" style={{ color: "var(--text-secondary)" }}>
            Immutable, tamper-resistant security audit trails for SOC 2 Type 2 & ISO/IEC 27001 compliance verification.
          </p>
        </div>

        <div style={{ display: "flex", gap: "0.75rem" }}>
          <button type="button" className="btn btn-secondary" onClick={handleExportCSV}>
            <Download size={16} style={{ marginRight: "0.4rem" }} />
            Export CSV
          </button>
          <button type="button" className="btn btn-primary" onClick={handleExportJSON}>
            <FileText size={16} style={{ marginRight: "0.4rem" }} />
            Export Auditor JSON
          </button>
        </div>
      </div>

      {/* Metric Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
        <div className="card glass-card" style={{ padding: "1.25rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--text-secondary)", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
            <span>Total Events</span>
            <Activity size={18} style={{ color: "var(--primary)" }} />
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "var(--text-primary)" }}>
            {totalEvents}
          </div>
        </div>

        <div className="card glass-card" style={{ padding: "1.25rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--text-secondary)", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
            <span>MFA Events</span>
            <ShieldCheck size={18} style={{ color: "var(--success)" }} />
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "var(--success)" }}>
            {mfaEventsCount}
          </div>
        </div>

        <div className="card glass-card" style={{ padding: "1.25rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--text-secondary)", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
            <span>Security Alerts</span>
            <AlertTriangle size={18} style={{ color: "#ff9800" }} />
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#ff9800" }}>
            {criticalCount}
          </div>
        </div>

        <div className="card glass-card" style={{ padding: "1.25rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--text-secondary)", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
            <span>Failed Logins</span>
            <Lock size={18} style={{ color: "#ef4444" }} />
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#ef4444" }}>
            {failedLoginsCount}
          </div>
        </div>
      </div>

      {/* Filters Toolbar */}
      <div className="card glass-card" style={{ padding: "1.25rem", marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center" }}>
          {/* Search Input */}
          <div className="input-with-icon" style={{ flex: "1 1 250px" }}>
            <Search size={16} className="input-icon" />
            <input
              type="text"
              className="form-input icon-padding"
              placeholder="Search user, action, or details..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Severity Filter */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>Severity:</span>
            <select
              className="form-input"
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              style={{ width: "auto" }}
            >
              <option value="All">All Severities</option>
              <option value="INFO">Info</option>
              <option value="WARNING">Warning</option>
              <option value="CRITICAL">Critical</option>
            </select>
          </div>

          {/* Category Filter */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>Category:</span>
            <select
              className="form-input"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{ width: "auto" }}
            >
              <option value="All">All Categories</option>
              <option value="AUTH">Authentication</option>
              <option value="USER_MANAGEMENT">User Management</option>
              <option value="SECURITY">Security</option>
              <option value="SYSTEM">System</option>
            </select>
          </div>

          {/* Time Filter */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>Range:</span>
            <select
              className="form-input"
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              style={{ width: "auto" }}
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="all">All Time</option>
            </select>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="card glass-card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-secondary)" }}>
            <Loader2 className="animate-spin" size={32} style={{ margin: "0 auto 1rem auto" }} />
            <p>Loading security audit logs...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-secondary)" }}>
            <Shield size={48} style={{ opacity: 0.3, margin: "0 auto 1rem auto" }} />
            <h3>No Audit Logs Found</h3>
            <p style={{ fontSize: "0.9rem" }}>No records match your selected filters.</p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border-color)", backgroundColor: "rgba(255,255,255,0.02)" }}>
                  <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontSize: "0.8rem", color: "var(--text-secondary)" }}>Timestamp (UTC)</th>
                  <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontSize: "0.8rem", color: "var(--text-secondary)" }}>Action</th>
                  <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontSize: "0.8rem", color: "var(--text-secondary)" }}>Category</th>
                  <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontSize: "0.8rem", color: "var(--text-secondary)" }}>Severity</th>
                  <th style={{ padding: "0.85rem 1rem", textAlign: "left", fontSize: "0.8rem", color: "var(--text-secondary)" }}>User Email</th>
                  <th style={{ padding: "0.85rem 1rem", textAlign: "right", fontSize: "0.8rem", color: "var(--text-secondary)" }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log) => {
                  const isExpanded = expandedLogId === log.id;
                  const formattedTime = formatAuditTimestamp(log.timestamp, log.clientTimestamp);

                  return (
                    <React.Fragment key={log.id}>
                      <tr 
                        style={{ 
                          borderBottom: "1px solid var(--border-color)",
                          cursor: "pointer",
                          transition: "background 0.2s ease"
                        }}
                        onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                      >
                        <td style={{ padding: "0.85rem 1rem", fontSize: "0.85rem", whiteSpace: "nowrap", fontFamily: "monospace" }}>
                          {formattedTime}
                        </td>
                        <td style={{ padding: "0.85rem 1rem", fontSize: "0.85rem", fontWeight: 600, color: "var(--text-primary)" }}>
                          {log.action}
                        </td>
                        <td style={{ padding: "0.85rem 1rem", fontSize: "0.8rem" }}>
                          <span style={{ padding: "0.2rem 0.5rem", borderRadius: "4px", background: "rgba(255,255,255,0.06)", color: "var(--text-secondary)" }}>
                            {log.category || "GENERAL"}
                          </span>
                        </td>
                        <td style={{ padding: "0.85rem 1rem", fontSize: "0.8rem" }}>
                          <span style={{ padding: "0.2rem 0.5rem", borderRadius: "12px", fontWeight: 600, ...getSeverityBadgeStyle(log.severity) }}>
                            {log.severity || "INFO"}
                          </span>
                        </td>
                        <td style={{ padding: "0.85rem 1rem", fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                          {log.userEmail || "System"}
                        </td>
                        <td style={{ padding: "0.85rem 1rem", textAlign: "right" }}>
                          <button 
                            type="button" 
                            className="btn-icon" 
                            style={{ width: "28px", height: "28px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                          >
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </td>
                      </tr>

                      {/* Expandable JSON details */}
                      {isExpanded && (
                        <tr style={{ backgroundColor: "rgba(0,0,0,0.2)", borderBottom: "1px solid var(--border-color)" }}>
                          <td colSpan={6} style={{ padding: "1rem" }}>
                            <div style={{ background: "var(--bg-main)", padding: "1rem", borderRadius: "8px", border: "1px solid var(--border-color)" }}>
                              <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "0.5rem" }}>
                                Complete Audit Payload (Immutable Record #{log.id}):
                              </div>
                              <pre style={{ margin: 0, fontSize: "0.8rem", color: "var(--primary)", fontFamily: "monospace", overflowX: "auto", whiteSpace: "pre-wrap" }}>
                                {JSON.stringify(log, null, 2)}
                              </pre>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
